import { useState } from 'react';
import {
  createTeamRole,
  deleteTeamRole,
  TeamRole,
  TeamRoleCategory,
  TeamRoleInput,
  updateTeamRole,
} from '../../../api/teams';

interface RoleItem {
  id: string;
  name: string;
  category: TeamRoleCategory;
  icon: string;
  description: string;
  origin: 'system' | 'custom';
}

interface Props {
  teamId: string;
  roles: TeamRole[];
  isAdmin: boolean;
  onRolesChange: () => Promise<void>;
}

const CATEGORIES = [
  { id: 'ministerial', name: 'Ministerial' },
  { id: 'vocal', name: 'Vocal' },
  { id: 'instrumental', name: 'Instrumental' },
  { id: 'tecnica', name: 'Técnica' },
  { id: 'apoio', name: 'Apoio' },
];

const ROLE_COLORS: Record<string, string> = {
  ministerial: '#C8A96E',
  vocal: '#7EB8A4',
  instrumental: '#A97EC8',
  tecnica: '#7E9EC8',
  apoio: '#E8A44C',
};

export default function RolesView({ teamId, roles: teamRoles, isAdmin, onRolesChange }: Props) {
  const roles: RoleItem[] = teamRoles.map((role) => ({
    ...role,
    origin: role.is_default ? 'system' : 'custom',
  }));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [addModal, setAddModal] = useState(false);
  const [editing, setEditing] = useState<RoleItem | null>(null);
  const [form, setForm] = useState({ name: '', category: 'ministerial' as TeamRoleCategory, icon: '🎵', description: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const toggleCategory = (catId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  };

  const groupedRoles = CATEGORIES.map((cat) => ({
    ...cat,
    roles: roles.filter((r) => r.category === cat.id),
  }));

  const handleAdd = () => {
    setEditing(null);
    setForm({ name: '', category: 'ministerial', icon: '🎵', description: '' });
    setAddModal(true);
  };

  const handleEdit = (role: RoleItem) => {
    setForm({ name: role.name, category: role.category, icon: role.icon, description: role.description });
    setEditing(role);
    setAddModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTeamRole(teamId, id);
      await onRolesChange();
    } catch {
      setToast('Erro ao excluir papel');
      setTimeout(() => setToast(''), 2500);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const roleInput: TeamRoleInput = { ...form, name: form.name.trim() };
    setSaving(true);
    try {
      if (editing) await updateTeamRole(teamId, editing.id, roleInput);
      else await createTeamRole(teamId, roleInput);
      await onRolesChange();
      setAddModal(false);
      setEditing(null);
    } catch {
      setToast('Erro ao salvar papel');
      setTimeout(() => setToast(''), 2500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-white/5 bg-[#0D0C09]">
        <div>
          <h1 className="text-white font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
            Papéis
          </h1>
          <p className="text-white/30 text-xs mt-0.5">{roles.length} papéis cadastrados</p>
        </div>
        {isAdmin && <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-[#C8A96E] text-[#0A0A0F] font-semibold px-4 py-2 rounded-lg text-sm cursor-pointer hover:bg-[#D4B87A] transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line text-sm"></i>
          Adicionar
        </button>}
      </div>

      {/* Roles by Category */}
      <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
        <div className="max-w-2xl flex flex-col gap-2">
          {groupedRoles.map((group) => (
            <div key={group.id} className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleCategory(group.id)}
                className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.03] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: ROLE_COLORS[group.id] || '#8E8E8E' }}
                  />
                  <span className="text-white text-sm font-medium">{group.name}</span>
                  <span className="text-white/20 text-xs">{group.roles.length}</span>
                </div>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${collapsed.has(group.id) ? 'ri-arrow-left-s-line' : 'ri-arrow-down-s-line'} text-white/30 text-sm`} />
                </div>
              </button>

              {!collapsed.has(group.id) && (
                <div className="border-t border-white/5">
                  {group.roles.map((role) => (
                    <div key={role.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 last:border-b-0">
                      <span className="text-lg flex-shrink-0">{role.icon}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-white/80 text-sm">{role.name}</span>
                        <p className="text-white/20 text-xs mt-0.5">{role.description}</p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          role.origin === 'system'
                            ? 'bg-white/5 text-white/30'
                            : 'bg-[#7EB8A4]/10 text-[#7EB8A4]'
                        }`}
                      >
                        {role.origin === 'system' ? 'Sistema' : 'Customizado'}
                      </span>
                      {role.origin === 'custom' && isAdmin && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => handleEdit(role)}
                            className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white cursor-pointer"
                          >
                            <i className="ri-edit-line text-xs" />
                          </button>
                          <button
                            onClick={() => handleDelete(role.id)}
                            className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-red-400 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {addModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setAddModal(false)} />
          <div className="relative bg-[#1A1915] border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4 z-10">
            <h3 className="text-white font-semibold mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              {editing ? 'Editar Papel' : 'Adicionar Papel'}
            </h3>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">Nome</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nome do papel"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
              </div>
              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">Categoria</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as TeamRoleCategory })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#C8A96E]/50 transition-colors cursor-pointer"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.id} className="bg-[#1A1915]">{cat.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">Ícone (emoji)</label>
                <input
                  type="text"
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  placeholder="🎵"
                  maxLength={2}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
              </div>
              <div>
                <label className="text-white/40 text-xs uppercase tracking-widest mb-1.5 block">Descrição</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Breve descrição do papel"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 outline-none focus:border-[#C8A96E]/50 transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setAddModal(false)}
                className="flex-1 px-5 py-3 border border-white/10 text-white/60 hover:text-white rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-5 py-3 bg-[#C8A96E] text-[#0A0A0F] font-semibold rounded-xl text-sm cursor-pointer hover:bg-[#D4B87A] disabled:opacity-50 transition-colors whitespace-nowrap"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-[#1A1915] border border-white/10 rounded-xl px-5 py-3 text-white text-sm z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
